import React, { useEffect, useMemo, useCallback, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { makeStyles } from "@fluentui/react-components";
import { Add16Filled } from "@fluentui/react-icons";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../redux/store";
import { checkUserPermission } from "../../../../utils";
import { UserPermissionsList } from "../../../../config";
import {
  clearAllLiabilitiesData,
  getLiabilityListItems,
} from "../../../../redux/Case/liabilitySlice";
import ASCButton from "../../../../components/Button";
import NoDataFound from "../../../../components/NoDataFound/noDataFound";
import CaseList from "../CaseList";
import { normalizeCaseType, isCurrentStatus, getItemStatus } from "../../../../shared/caseActiveStatus";

// Route segments match the existing app-wide convention already used by
// getCaseCategoryByCaseType/radioOptions.liabilities — including the
// pre-existing "morgages" typo — do not rename them.
export const LIABILITY_CASE_TYPE_MAP: Record<string, string> = {
  morgages: "Mortgages",
  "loans-lease-hp": "Loans/Lease/HP",
  "credit-cards": "Credit Cards",
};

// All three Liability sub types share a single `Status` field.
export const LIABILITY_HAS_STATUS_FIELD: Record<string, boolean> = {
  morgages: true,
  "loans-lease-hp": true,
  "credit-cards": true,
};

type Phase =
  | "checking"
  | "has-non-current-only"
  | "truly-empty"
  | "navigating";

const useStyles = makeStyles({
  wrapper: {
    display: "flex",
    justifyContent: "flex-end",
    margin: "10px 10px 0",
  },
});

const LiabilityHomePage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { clientId, case_type, action } = useParams<{
    clientId: string;
    case_type?: string;
    action?: string;
  }>();

  const { authUser, liabilities } = useSelector((state: RootState) => ({
    authUser: state.authUser,
    liabilities: state.liabilities,
  }));

  const styles = useStyles();

  useEffect(() => {
    if (clientId && !case_type) {
      navigate(`/clients/${clientId}/liabilities/morgages`, { replace: true });
    }
  }, [clientId, case_type, navigate]);

  const dbCaseType = case_type ? LIABILITY_CASE_TYPE_MAP[case_type] : undefined;

  const [phase, setPhase] = useState<Phase>("checking");
  const requestIdRef = useRef(0);

  useEffect(() => {
    setPhase("checking");
  }, [case_type]);

  useEffect(() => {
    if (!clientId || !case_type || phase !== "checking") return;

    const requestId = ++requestIdRef.current;

    dispatch(clearAllLiabilitiesData());
    dispatch(getLiabilityListItems({ customerId: clientId, caseType: dbCaseType }))
      ?.then?.((result: any) => {
        if (requestIdRef.current !== requestId) return;
        const items = Array.isArray(result?.payload) ? result.payload : [];
        const byType = dbCaseType
          ? items.filter((x: any) => normalizeCaseType(x.case_type) === normalizeCaseType(dbCaseType))
          : items;

        if (byType.length === 0) {
          setPhase("truly-empty");
        } else if (byType.some((x: any) => isCurrentStatus("liability", getItemStatus(x)))) {
          setPhase("navigating");
        } else {
          setPhase("has-non-current-only");
        }
      });
  }, [clientId, case_type, dbCaseType, phase, dispatch]);

  const matchingAllItems = useMemo(() => {
    return dbCaseType
      ? liabilities.listItems.filter((item: any) => normalizeCaseType(item.case_type) === normalizeCaseType(dbCaseType))
      : liabilities.listItems;
  }, [liabilities.listItems, dbCaseType]);

  const matchingCurrentItems = useMemo(() => {
    return matchingAllItems.filter((item: any) => isCurrentStatus("liability", getItemStatus(item)));
  }, [matchingAllItems]);

  useEffect(() => {
    if (phase !== "navigating" || !case_type) return;
    const target = matchingCurrentItems.length > 0 ? matchingCurrentItems[0] : matchingAllItems[0];
    if (!target) return;
    navigate(
      `/clients/${clientId}/liabilities/${case_type}/${target?.liability_id}/liability/${action || "view"}`
    );
  }, [clientId, case_type, action, navigate, matchingCurrentItems, matchingAllItems, phase]);

  const handleAddNew = useCallback(() => {
    navigate(`/clients/${clientId}/liabilities/${case_type}/add`);
  }, [clientId, case_type, navigate]);

  if (!case_type || liabilities.loading) return null;
  if (phase === "checking" || phase === "navigating") return null;

  const addNewButton = checkUserPermission(
    authUser,
    UserPermissionsList.ALLOW_UPDATE_CLIENT
  ) && (
    <ASCButton
      shape="rounded"
      appearance="primary"
      className="asc-button-primary"
      icon={<Add16Filled />}
      size="medium"
      onClick={handleAddNew}
      label="Add New"
    />
  );

  if (phase === "has-non-current-only") {
    return (
      <div>
        <div className={styles.wrapper}>{addNewButton}</div>
        <div style={{ marginTop: "10px" }}>
          <CaseList
            currentRecord={1}
            setCurrentRecord={() => {}}
            module="liabilities"
            data={[]}
            activeItem={null}
            section="liabilities"
            tab="liability"
            showNonCurrent={false}
            onToggleNonCurrent={() => setPhase("navigating")}
            emptyMessage='No active records for current cases. Click "Non-current Cases" to view non-current records.'
          />
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className={styles.wrapper}>{addNewButton}</div>
      <NoDataFound />
    </div>
  );
};

export default LiabilityHomePage;
